import { useState } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { formaterDate, formaterMontant } from '../lib/format';
import { Badge, Champ, Infobulle } from './Elements';
import type { NatureSalaire, SalaireContrat } from '../types';

/**
 * Le SALAIRE MENSUEL FIXE d'un contrat, par periode (2026-10-02) : celui
 * d'un administratif, ou d'un professeur ou animateur a temps plein.
 *
 * Meme grammaire que les variables de paie de l'ecole : une periode sans
 * fin vaut pour les mois a venir, en ajouter une ferme la precedente a la
 * veille, et une periode qui a servi a un mois de paie cloture garde son
 * montant. Le bloc ecrit immediatement — chaque geste est un acte a
 * part —, sans passer par le bouton Enregistrer du contrat.
 *
 * `avecHeures` : l'administratif a TEMPS PARTIEL saisit ses heures par
 * semaine AVEC le salaire. Elles vivent sur la periode, pas sur le
 * contrat : changer d'horaire, c'est poser une nouvelle periode avec son
 * nouveau net, et l'ancienne garde la trace.
 */

const enEuros = (centimes: number) => (centimes / 100).toFixed(2).replace('.', ',');
const enCentimes = (texte: string): number | null => {
  const n = Number(texte.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && texte.trim() !== '' ? Math.round(n * 100) : null;
};

/** Heures par semaine d'un temps partiel : au-dessus de zero, sous le temps plein. */
const enHeures = (texte: string, plafond: number): number | null => {
  const n = Number(texte.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n < plafond && texte.trim() !== '' ? n : null;
};
const formaterHeures = (heures: number) => `${heures.toLocaleString('fr-FR')} h`;

export function SalairesContrat({
  contratId,
  peutGerer,
  avecHeures = false,
  plafondHeures = 35,
}: {
  contratId: string;
  peutGerer: boolean;
  avecHeures?: boolean;
  plafondHeures?: number;
}) {
  const salaires = useRessource(
    () => api.get<SalaireContrat[]>(`/api/contrats/${contratId}/salaires`),
    [contratId],
  );
  // NET ou BRUT (2026-10-16) : le montant saisi est l'un ou l'autre, rien
  // n'est converti. Net par defaut — c'est ce que le CEA declare.
  const [saisie, setSaisie] = useState<{
    montant: string;
    nature: NatureSalaire;
    heures: string;
    dateDebut: string;
    dateFin: string;
  }>({ montant: '', nature: 'net', heures: '', dateDebut: '', dateFin: '' });
  const [edition, setEdition] = useState<{
    id: string;
    montant: string;
    nature: NatureSalaire;
    heures: string;
    dateFin: string;
  } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const messageHeures = `Indiquez les heures par semaine : plus de 0, moins de ${plafondHeures}.`;

  const agir = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      salaires.recharger();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const ajouter = () => {
    const montant = enCentimes(saisie.montant);
    if (montant === null || !saisie.dateDebut) {
      setErreur('Indiquez un montant et une date de début.');
      return;
    }
    const heures = avecHeures ? enHeures(saisie.heures, plafondHeures) : null;
    if (avecHeures && heures === null) {
      setErreur(messageHeures);
      return;
    }
    void agir(async () => {
      await api.post(`/api/contrats/${contratId}/salaires`, {
        montant,
        nature: saisie.nature,
        dateDebut: saisie.dateDebut,
        dateFin: saisie.dateFin || null,
        ...(avecHeures ? { heuresSemaine: heures } : {}),
      });
      setSaisie({ montant: '', nature: 'net', heures: '', dateDebut: '', dateFin: '' });
    });
  };

  const enregistrerEdition = () => {
    if (!edition) return;
    const montant = enCentimes(edition.montant);
    if (montant === null) {
      setErreur('Montant invalide : utilisez le format 1 850,00.');
      return;
    }
    const heures = avecHeures ? enHeures(edition.heures, plafondHeures) : null;
    if (avecHeures && heures === null) {
      setErreur(messageHeures);
      return;
    }
    void agir(async () => {
      await api.patch(`/api/contrats/${contratId}/salaires/${edition.id}`, {
        montant,
        nature: edition.nature,
        dateFin: edition.dateFin || null,
        ...(avecHeures ? { heuresSemaine: heures } : {}),
      });
      setEdition(null);
    });
  };

  const supprimer = (s: SalaireContrat) => {
    if (!window.confirm('Supprimer cette période de salaire ?')) return;
    void agir(() => api.delete(`/api/contrats/${contratId}/salaires/${s.id}`));
  };

  const aujourdhui = new Date().toISOString().slice(0, 10);
  const enCoursDeValidite = (s: SalaireContrat) =>
    s.date_debut <= aujourdhui && (s.date_fin === null || s.date_fin >= aujourdhui);

  // La periode la plus recente en tete, l'historique dessous.
  const lignes = [...(salaires.donnees ?? [])].sort((a, b) => b.date_debut.localeCompare(a.date_debut));

  // La colonne des heures reste visible tant qu'une periode en porte :
  // un contrat repasse a temps plein garde la trace de son temps partiel.
  const montrerHeures = avecHeures || lignes.some((s) => s.heures_semaine !== null);
  const colonnes = montrerHeures ? 6 : 5;

  return (
    <div className="space-y-3">
      <div className="etiquette">Salaire mensuel</div>

      {peutGerer && (
        <div
          className={`grid items-end gap-2 ${
            avecHeures
              ? 'grid-cols-[8rem_5.5rem_6rem_9rem_9rem_auto]'
              : 'grid-cols-[8rem_5.5rem_9rem_9rem_auto]'
          }`}
        >
          <Champ libelle="Montant (€)">
            <input
              className="champ tabulaire py-1.5 text-right"
              placeholder="1 850,00"
              inputMode="decimal"
              value={saisie.montant}
              onChange={(e) => setSaisie({ ...saisie, montant: e.target.value })}
            />
          </Champ>
          <Champ libelle="Net / brut">
            <select
              className="champ py-1.5"
              aria-label="Nature du montant"
              value={saisie.nature}
              onChange={(e) => setSaisie({ ...saisie, nature: e.target.value as NatureSalaire })}
            >
              <option value="net">Net</option>
              <option value="brut">Brut</option>
            </select>
          </Champ>
          {avecHeures && (
            <Champ libelle="Heures / sem.">
              <input
                className="champ tabulaire py-1.5 text-right"
                placeholder="28"
                inputMode="decimal"
                value={saisie.heures}
                onChange={(e) => setSaisie({ ...saisie, heures: e.target.value })}
              />
            </Champ>
          )}
          <Champ libelle="Du">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateDebut}
              onChange={(e) => setSaisie({ ...saisie, dateDebut: e.target.value })}
            />
          </Champ>
          <Champ libelle="Au">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateFin}
              min={saisie.dateDebut || undefined}
              onChange={(e) => setSaisie({ ...saisie, dateFin: e.target.value })}
            />
          </Champ>
          <div className="block">
            <span className="etiquette invisible">Ajouter</span>
            <button type="button" className="bouton-secondaire block py-1.5" disabled={enCours} onClick={ajouter}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      {salaires.erreur && <p className="text-sm text-red-700">{salaires.erreur}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="py-1 pr-2 text-right font-medium">Montant</th>
            <th className="py-1 pr-2 font-medium">Nature</th>
            {montrerHeures && <th className="py-1 pr-2 text-right font-medium">Heures / sem.</th>}
            <th className="py-1 pr-2 font-medium">Du</th>
            <th className="py-1 pr-2 font-medium">Au</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {lignes.length === 0 && !salaires.chargement && (
            <tr>
              <td colSpan={colonnes} className="py-2 text-encre-400">
                Aucun salaire saisi : rien ne sera versé au titre du fixe.
              </td>
            </tr>
          )}
          {lignes.map((s) =>
            edition?.id === s.id ? (
              <tr key={s.id} className="bg-encre-50">
                <td className="py-1.5 pr-2">
                  <input
                    className="champ tabulaire py-1 text-right"
                    value={edition.montant}
                    onChange={(e) => setEdition({ ...edition, montant: e.target.value })}
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <select
                    className="champ py-1"
                    aria-label="Nature du montant"
                    value={edition.nature}
                    onChange={(e) => setEdition({ ...edition, nature: e.target.value as NatureSalaire })}
                  >
                    <option value="net">Net</option>
                    <option value="brut">Brut</option>
                  </select>
                </td>
                {montrerHeures && (
                  <td className="py-1.5 pr-2 text-right">
                    {avecHeures ? (
                      <input
                        className="champ tabulaire py-1 text-right"
                        aria-label="Heures par semaine"
                        value={edition.heures}
                        onChange={(e) => setEdition({ ...edition, heures: e.target.value })}
                      />
                    ) : (
                      <span className="tabulaire text-encre-500">
                        {s.heures_semaine === null ? '—' : formaterHeures(s.heures_semaine)}
                      </span>
                    )}
                  </td>
                )}
                <td className="tabulaire py-1.5 pr-2 text-encre-500">{formaterDate(s.date_debut)}</td>
                <td className="py-1.5 pr-2">
                  <input
                    type="date"
                    className="champ py-1"
                    value={edition.dateFin}
                    min={s.date_debut}
                    onChange={(e) => setEdition({ ...edition, dateFin: e.target.value })}
                  />
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={enregistrerEdition}>
                    Enregistrer
                  </button>
                  <button type="button" className="ml-2 text-xs text-encre-500 underline" onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={s.id} className={s.verrouillee ? 'text-encre-400' : ''}>
                <td className="tabulaire py-1.5 pr-2 text-right">
                  {formaterMontant(s.montant)}
                  {enCoursDeValidite(s) && (
                    <span className="ml-2">
                      <Badge ton="succes">en cours</Badge>
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-2">{s.nature === 'brut' ? 'Brut' : 'Net'}</td>
                {montrerHeures && (
                  <td className="tabulaire py-1.5 pr-2 text-right">
                    {s.heures_semaine === null ? '—' : formaterHeures(s.heures_semaine)}
                  </td>
                )}
                <td className="tabulaire py-1.5 pr-2">{formaterDate(s.date_debut)}</td>
                <td className="tabulaire py-1.5 pr-2">{s.date_fin ? formaterDate(s.date_fin) : '—'}</td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {s.verrouillee ? (
                    <Infobulle texte="Cette période a servi à une paie clôturée : elle ne se modifie plus.">
                      <span className="text-xs">🔒</span>
                    </Infobulle>
                  ) : (
                    peutGerer && (
                      <>
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() =>
                            setEdition({
                              id: s.id,
                              montant: enEuros(s.montant),
                              nature: s.nature,
                              heures: s.heures_semaine === null ? '' : String(s.heures_semaine).replace('.', ','),
                              dateFin: s.date_fin ?? '',
                            })
                          }
                        >
                          Modifier
                        </button>
                        <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={() => supprimer(s)}>
                          Supprimer
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      {erreur && <p className="text-sm text-red-700">{erreur}</p>}

      <p className="text-xs text-encre-500">
        Montant mensuel, net ou brut selon ce que vous connaissez : rien n’est converti, la paie
        souligne les montants bruts. Une période sans date de fin vaut pour les mois à
        venir ; en ajouter une ferme la précédente à la veille. Une période qui a servi à un mois
        de paie clôturé ne se modifie plus.
        {avecHeures &&
          ` Les heures se saisissent avec le salaire, sous ${plafondHeures} h : changer d’horaire, c’est poser une nouvelle période, et l’ancienne garde la trace.`}
      </p>
    </div>
  );
}
